import React from 'react';
import { Volume2, User, UserCircle } from 'lucide-react';

// OpenAI TTS Voice options with gender labels
export const VOICE_OPTIONS = [
  { value: 'shimmer', label: 'Shimmer', gender: 'female', description: 'Expressive & Story-telling' },
  { value: 'nova', label: 'Nova', gender: 'female', description: 'Warm & Friendly' },
  { value: 'alloy', label: 'Alloy', gender: 'neutral', description: 'Balanced & Clear' },
  { value: 'echo', label: 'Echo', gender: 'male', description: 'Clear & Natural' },
  { value: 'onyx', label: 'Onyx', gender: 'male', description: 'Deep & Calm' },
  { value: 'fable', label: 'Fable', gender: 'male', description: 'British & Narrative' },
];

export const getVoicesByGender = (gender) => {
  if (gender === 'all') return VOICE_OPTIONS;
  return VOICE_OPTIONS.filter(v => v.gender === gender);
};

export const getVoiceLabel = (voiceValue) => {
  const voice = VOICE_OPTIONS.find(v => v.value === voiceValue);
  return voice ? `${voice.label} (${voice.gender === 'neutral' ? 'Neutral' : voice.gender === 'female' ? 'Female' : 'Male'})` : voiceValue;
};

const VoiceSelector = ({ 
  voice, 
  onVoiceChange, 
  speed, 
  onSpeedChange, 
  showSpeed = true,
  compact = false,
  className = '',
  theme = 'light' // 'light' or 'dark'
}) => {
  const bgClass = theme === 'dark' 
    ? 'bg-slate-800/50 border-slate-600' 
    : 'bg-white border-slate-200';
  const textClass = theme === 'dark' ? 'text-slate-200' : 'text-slate-700';
  const labelClass = theme === 'dark' ? 'text-slate-400' : 'text-slate-500';
  const selectBgClass = theme === 'dark'
    ? 'bg-slate-700 border-slate-600 text-white'
    : 'bg-white border-slate-200 text-slate-800';

  if (compact) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div className="flex items-center gap-2">
          <Volume2 className={`w-4 h-4 ${labelClass}`} />
          <select
            value={voice}
            onChange={(e) => onVoiceChange(e.target.value)}
            className={`text-xs ${selectBgClass} border rounded px-2 py-1`}
            data-testid="voice-selector"
          >
            <optgroup label="Female Voices">
              {getVoicesByGender('female').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
            <optgroup label="Male Voices">
              {getVoicesByGender('male').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
            <optgroup label="Neutral">
              {getVoicesByGender('neutral').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
          </select>
        </div>
        {showSpeed && (
          <div className="flex items-center gap-2">
            <span className={`text-xs ${labelClass}`}>Speed:</span>
            <input
              type="range"
              min="0.5"
              max="1.2"
              step="0.05"
              value={speed}
              onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
              className="w-16 h-2 accent-emerald-500"
              data-testid="speed-slider"
            />
            <span className={`text-xs px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-slate-600 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
              {speed}x
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${bgClass} border rounded-xl p-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-4">
        {/* Voice Selection */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Volume2 className={`w-4 h-4 ${labelClass}`} />
            <span className={`text-xs font-medium ${labelClass}`}>Voice:</span>
          </div>
          <select
            value={voice}
            onChange={(e) => onVoiceChange(e.target.value)}
            className={`text-sm ${selectBgClass} border rounded-lg px-3 py-1.5`}
            data-testid="voice-selector"
          >
            <optgroup label="Female Voices">
              {getVoicesByGender('female').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
            <optgroup label="Male Voices">
              {getVoicesByGender('male').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
            <optgroup label="Neutral">
              {getVoicesByGender('neutral').map(v => (
                <option key={v.value} value={v.value}>{v.label} - {v.description}</option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Speed Control */}
        {showSpeed && (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${labelClass}`}>Speed:</span>
            <input
              type="range"
              min="0.5"
              max="1.2"
              step="0.05"
              value={speed}
              onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
              className="w-24 h-2 accent-emerald-500"
              data-testid="speed-slider"
            />
            <span className={`text-xs px-2 py-1 rounded-lg font-medium ${theme === 'dark' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {speed}x
            </span>
          </div>
        )}
      </div>
      
      {/* Current Voice Info */}
      <div className={`mt-2 text-xs ${labelClass} flex items-center gap-1`}>
        {VOICE_OPTIONS.find(v => v.value === voice)?.gender === 'female' ? (
          <UserCircle className="w-3 h-3" />
        ) : VOICE_OPTIONS.find(v => v.value === voice)?.gender === 'male' ? (
          <User className="w-3 h-3" />
        ) : null}
        <span>
          {getVoiceLabel(voice)} • Slower is easier for children to understand
        </span>
      </div>
    </div>
  );
};

export default VoiceSelector;
